import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { yellow } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { unstable_usePrompt } from "react-router-dom";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import NoMergeTagsWarningDialog from "features/Influencer/Templates/TemplatesTable/NoMergeTagsWarningDialog";
import { fetcherAuth } from "utils/api";
import { containsMergeTagsTemplates } from "utils/string";

interface Props {
  open: boolean;
  handleClose: () => void;
  editTemplate: Template;
  setEditTemplate: Dispatch<SetStateAction<Template>>;
  handleNext?: (newlyCreatedTemplate?: Template) => void;
  allowChangeTitle?: boolean;
}

const CreateTemplateSetUp = ({
  open,
  handleClose,
  editTemplate,
  setEditTemplate,
  handleNext,
  allowChangeTitle = false,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const [isDirty, setIsDirty] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const { setTemplates } = useContext(OutreachTemplatesContext);
  const [openMergeTagWarning, setOpenMergeTagWarning] = useState(false);

  const createTemplate = async () => {
    if (!currentOrg?.id) return;
    setIsDirty(false);

    if (
      !openMergeTagWarning &&
      !containsMergeTagsTemplates(editTemplate?.body || "")
    ) {
      setOpenMergeTagWarning(true);
      return;
    }

    try {
      setCreateLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        { ...editTemplate, validateBulkSendVariables: true },
      );
      setEditTemplate(res.template);
      setAlert("Template Saved", "success");
      setOpenMergeTagWarning(false);
      setTemplates((prev) => [res.template, ...prev]);
      if (handleNext) {
        handleNext(res.template);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  unstable_usePrompt({
    message:
      "You have an unsaved template. Are you sure you want to navigate away from this page?",
    when: ({ currentLocation, nextLocation }) =>
      Boolean(isDirty) && currentLocation.pathname !== nextLocation.pathname,
  });

  return (
    <>
      <Dialog
        fullScreen={isMobileScreen}
        open={open}
        fullWidth
        maxWidth={
          editTemplate?.contentType === TemplateContentType.FOLLOWUP
            ? "sm"
            : "md"
        }
        onClose={handleClose}
      >
        <DialogTitle sx={{ justifyContent: "center", display: "flex" }}>
          {editTemplate?.name || "Your First Pitch Template"}
        </DialogTitle>
        <DialogContent>
          <>
            {editTemplate?.contentType === TemplateContentType.OUTREACH && (
              <>
                Some tips for your template:
                <ul>
                  <li style={{ listStyleType: "none" }}>
                    <Typography>
                      <Box
                        component="i"
                        className="fa-solid fa-sparkles"
                        sx={{ pr: 1, color: yellow[700] }}
                      />
                      You can use {"{Brand Name}"} and {"{First Name}"} as
                      placeholders—each email will be personalized for the brand
                      and recipient. Do not include any other variables in your
                      templates.
                    </Typography>
                  </li>
                  <li style={{ listStyleType: "none" }}>
                    <Typography>
                      <Box
                        component="i"
                        className="fa-solid fa-file-pen"
                        sx={{ pr: 1, color: "primary.main" }}
                      />
                      You can modify your template after you create it.
                    </Typography>
                  </li>
                </ul>
                <Divider sx={{ my: 3 }} />
              </>
            )}
            <Box display="flex" justifyContent="center">
              <Box sx={{ width: 600 }}>
                <TemplateBody
                  editTemplate={editTemplate}
                  setEditTemplate={setEditTemplate}
                  isNew={true}
                  allowChangeTitle={allowChangeTitle}
                  minimalist
                  displayError={true}
                />
              </Box>
            </Box>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>

          <LoadingButton
            loading={createLoading}
            color="primary"
            disabled={!editTemplate.body}
            onClick={createTemplate}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <NoMergeTagsWarningDialog
        open={openMergeTagWarning}
        handleClose={() => setOpenMergeTagWarning(false)}
        onProceed={createTemplate}
      />
    </>
  );
};

export default CreateTemplateSetUp;
